import React from 'react';
import { useSelector } from 'react-redux';

import { L10nNumber } from '@travel/i18n';
import { RakutenPoints } from '@travel/icons/ui';
import Translate from '@travel/traveler-core/components/Translate';
import { PROMOTION_CLASS } from '@travel/traveler-core/constants/salesPromotion';
import { useDeviceTypes, usePriceLabel } from '@travel/traveler-core/hooks';
import type {
  PlanPaymentRestrictionFeature,
  Price,
} from '@travel/traveler-core/types/providerRatePlan';
import { Tag } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import Money from 'components/Money';
import PlanPaymentRestriction from 'components/PlanPaymentRestriction';
import PriceFor from 'components/PriceFor';
import DirectPromotionBadge from 'components/PriceLabel/DirectPromotionBadge';
import type {
  InternalSalesPromotionFormQuery,
  SalesPromotionFormQuery,
} from 'components/SalesPromotionInput';

import { getMember } from 'store/member/selectors';
import { getIsDirectContractType } from 'store/providerInformation/selectors';

import { usePrice } from 'hooks/usePrice';
import type { RoomFormQuery } from 'ProviderList-Types';

import ReserveButton from '../ReserveButton';

import styles from './roomPlanItemCard.module.scss';

type Props = {
  price: Price;
  points?: {
    amount: number;
    currency: string;
  };
  paymentRestrictionFeature?: PlanPaymentRestrictionFeature;
  searchParam: RoomFormQuery & SalesPromotionFormQuery & InternalSalesPromotionFormQuery;
  onReserve: () => void;
  onSelectDate?: (startDate?: string, endDate?: string) => void;
  itemRatePlanId: string;
  isRakutenMembership?: boolean;
};

function PriceInfo(props: Props) {
  const {
    searchParam,
    price,
    points,
    paymentRestrictionFeature,
    onReserve,
    onSelectDate,
    isRakutenMembership,
  } = props;

  const { isSP } = useDeviceTypes();
  const { isMember } = useSelector(getMember);

  const { guestLabel, nightLabel, chargeUnitLabel, roomsLabelText } = usePriceLabel(searchParam);
  const isDirectContractType = useSelector(getIsDirectContractType);
  const { unitPrice, basePrice, totalPrice, priceWithoutDiscount, isConsumptionTaxIncluded } =
    usePrice(price, isDirectContractType);

  const roundPosition = isDirectContractType ? null : -1;
  const shouldShowReserveButton = isMember || !isRakutenMembership;

  const taxesLabel = (
    <Translate
      className={styles.label}
      id={
        isConsumptionTaxIncluded
          ? 'Provider_List.Search_Results.Taxes'
          : 'Provider_List.Luxury.Tax_Excluded'
      }
    />
  );

  const roomsPerNightLabel = (
    <Translate id="Provider_List.Out_To_Out.Search_Results.Per_Room_Per_Night" />
  );
  const totalLabel = <Translate id="Provider_List.Out_To_Out.Search_Results.Total" />;

  const mainSalesPromotion = price.salesPromotions?.find(
    (sp) => sp.promotionClass === PROMOTION_CLASS.MAIN,
  );
  const subSalesPromotion = price.salesPromotions?.find(
    (sp) => sp.promotionClass === PROMOTION_CLASS.SUB,
  );
  const isSameSalesPromotionRepresentativeName =
    mainSalesPromotion &&
    subSalesPromotion &&
    mainSalesPromotion.representativeFeatureName === subSalesPromotion.representativeFeatureName;

  const priceAndTax = (
    <div
      className={cx(
        styles.priceAndTax,
        isSP && styles.flexRowReverse,
        !isDirectContractType && styles.indirectContractProvider,
        !isDirectContractType && isSP && styles.spaceBetween,
      )}
    >
      {' '}
      <div>
        {((isNotEmptyArray(price.salesPromotions) && basePrice && basePrice !== totalPrice) ||
          (isNotEmptyArray(price.salesPromotions) &&
            priceWithoutDiscount &&
            priceWithoutDiscount !== unitPrice)) && (
          <div>
            <div
              className={cx(styles.basePriceWrapper, !isDirectContractType && styles.noMarginTop)}
            >
              {!!price.discountRate && price.discountRate >= 2 && (
                <Tag
                  type="filled"
                  color="scarlet"
                  className={styles.discountTag}
                  data-testid="priceLabel-discountRate-tag"
                >
                  {<>-{price.discountRate}%</>}
                </Tag>
              )}
              <span className={styles.basePrice} data-testid="priceInfo-basePrice">
                <Money
                  value={isDirectContractType ? basePrice : (priceWithoutDiscount as number)}
                  className={styles.priceText}
                  roundPosition={roundPosition}
                />
              </span>
            </div>
          </div>
        )}
        <div className={cx(isSP && styles.alignRight, !isDirectContractType && styles.flexColumn)}>
          <div className={styles.unitPrice} data-testid="priceInfo-unitPrice">
            <Money
              value={isDirectContractType ? totalPrice : (unitPrice as number)}
              className={styles.priceText}
              roundPosition={roundPosition}
            />
          </div>
          {Boolean(chargeUnitLabel) && (
            <div className={styles.label} data-testid="priceInfo-chargeUnit-label">
              {chargeUnitLabel}
            </div>
          )}
          {!isDirectContractType && roomsPerNightLabel}
          {taxesLabel}
        </div>
      </div>
      {!isDirectContractType && (
        <div>
          <div
            className={cx(styles.totalPrice, !isSP && styles.alignRights)}
            data-testid="priceInfo-totalPrice"
          >
            {totalLabel}&nbsp;{' '}
            <Money value={totalPrice} className={styles.priceText} roundPosition={roundPosition} />
          </div>
          {Boolean(guestLabel && nightLabel) && (
            <div className={styles.label}>
              {guestLabel} <>&nbsp;{nightLabel}&nbsp;</>
              {roomsLabelText && <>{roomsLabelText}</>}
            </div>
          )}
          {isSP && !isMember && (
            <Translate
              className={styles.remindMessage}
              id="Provider_Info.Reserve.No_Account_Needed"
            />
          )}
        </div>
      )}
    </div>
  );

  const promotionTags = isNotEmptyArray(price.salesPromotions) && (
    <div className={cx(styles.tagsWrapper, isDirectContractType && styles.alignRight)}>
      {mainSalesPromotion && (
        <DirectPromotionBadge
          key={'main'}
          className={styles.directPromotion}
          salesPromotion={mainSalesPromotion}
          planPaymentRestrictionFeature={paymentRestrictionFeature}
        />
      )}
      {subSalesPromotion && !isSameSalesPromotionRepresentativeName && (
        <DirectPromotionBadge
          className={styles.directPromotion}
          key={'sub'}
          salesPromotion={subSalesPromotion}
        />
      )}
    </div>
  );

  const reservationButton = (
    <div className={styles.reserveWrapper}>
      <ReserveButton
        wrapperClassName={styles.reserveButtonWrapper}
        className={styles.reserveButton}
        onClick={onReserve}
        searchParam={searchParam}
        onSelectDate={onSelectDate}
        shouldShowVacancyCalendar={isDirectContractType}
        itemRatePlanId={props.itemRatePlanId}
      />
    </div>
  );

  const priceLabels = (
    <div className={styles.labelsWrapper}>
      {Boolean(chargeUnitLabel) && (
        <div className={styles.label} data-testid="priceLabel-chargeUnit-label">
          {chargeUnitLabel}
        </div>
      )}
      {Boolean(guestLabel && nightLabel) && (
        <div className={styles.label}>
          <PriceFor
            rooms={Number(searchParam.noOfUnits)}
            adults={Number(searchParam.adults)}
            childrenAges={
              props.searchParam?.childrenAges
                ? props.searchParam?.childrenAges.split(',').map(Number)
                : []
            }
            startDate={searchParam.startDate}
            endDate={searchParam.endDate}
            shouldShowIcon={!isSP}
            title={<Translate id="Provider_Info.Basic_Info.Hotel_Info.Price_For" />}
          />
        </div>
      )}
    </div>
  );

  const paymentRestriction = paymentRestrictionFeature && (
    <Tag type="filled" color="gray" className={styles.paymentRestrictionTag}>
      <PlanPaymentRestriction
        className={styles.paymentRestriction}
        {...paymentRestrictionFeature}
      />
    </Tag>
  );

  const pointItem = Number(points?.amount) > 0 && (
    <span className={styles.pointItem}>
      <RakutenPoints size={16} />
      <Translate
        id="Provider_Info.Room_List.Plan_Info.Points"
        data={{
          points_calculation: <L10nNumber value={points?.amount || 0} />,
          currency: `${points?.currency || ''}`,
        }}
      />
    </span>
  );

  return (
    <>
      {isSP && promotionTags}
      {isDirectContractType ? (
        <div className={styles.itemPriceWrapper}>
          <div className={styles.priceInfoWrapper}>
            {isSP ? (
              <>
                <div className={styles.priceAndTagsWrapper}>
                  {priceAndTax}
                  {priceLabels}
                  {paymentRestriction}
                  {pointItem}
                </div>
                {!isMember && !isRakutenMembership && (
                  <Translate
                    className={styles.remindMessage}
                    id="Provider_Info.Reserve.No_Account_Needed"
                  />
                )}
              </>
            ) : (
              <>
                <div className={styles.InfoTagsWrapper}>
                  {promotionTags}
                  {(paymentRestriction || pointItem) && (
                    <div className={styles.restrictionAndPoints}>
                      {paymentRestriction}
                      {pointItem}
                    </div>
                  )}
                </div>
                <div className={styles.labelWrapper}>{priceLabels}</div>
              </>
            )}
          </div>
          <div
            className={cx(styles.reserveWrapper, shouldShowReserveButton && styles.priceAndReserve)}
          >
            {!isSP && priceAndTax}
            <ReserveButton
              className={styles.reserveButton}
              onClick={onReserve}
              searchParam={searchParam}
              onSelectDate={onSelectDate}
              shouldShowVacancyCalendar={true}
              itemRatePlanId={props.itemRatePlanId}
              isRakutenMembership={isRakutenMembership}
            />
          </div>
        </div>
      ) : (
        <div className={styles.priceTagWrapper}>
          {isSP && pointItem}
          {!isSP && promotionTags}
          {priceAndTax}
          {reservationButton}
        </div>
      )}
    </>
  );
}

export default PriceInfo;
